import { styled } from "@mui/material";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridEventListener,
  GridSlotsComponentsProps,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { hasPermission, IfPermission } from "..";
import Permissions from "../../constants/permissions";
import useAuth, { DataGridColumns, DataGridPreferences, DataGrids } from "../../context/UserContext";
import { Upload } from "../../graphql/types";
import { calculateDateDuration, getFormattedReportDate, toLocalAndUtcDates } from "../../lib/Util";
import { UploadAbandonButton } from "../AbandonButton";
import { ActionsMenu, CopyToClipboardButton } from "../ActionsMenu/ActionsMenu";
import { DatagridEmptyState } from "../DatagridEmptyState/DatagridEmptyState";
import {
  CustomColumnsPanel,
  customColumnWidthChange,
  customOnColumnOrderChange,
  getInitialSortModel,
} from "../DataGridUtils";
import { HumanDate } from "../Date";
import { PurposeCell } from "../DownloadsDataGrid/PurposeCell";
import { UploadStatusIcon } from "../UploadStatusIcon";
import { isFilteringUploads } from "./UploadFilters";
import { IUploadsFilter } from "./uploadsDatagridUtils";

const CenteredDiv = styled("div")({
  display: "flex",
  alignItems: "center",
  height: "100%",
});

const canUploadBeAbandoned = (status: string) => {
  return ["preparing", "queued", "uploading", "loading", "retrying", "failed"].indexOf(status) > -1;
};

const EmptyRows = ({ isFiltering, defaultText }: NonNullable<GridSlotsComponentsProps["noRowsOverlay"]>) => {
  return (
    <DatagridEmptyState
      Icon={InfoIcon}
      text={isFiltering ? "No results, try another filter" : defaultText || "You have no uploads yet"}
    />
  );
};

type UploadsDatagridProps = {
  rows?: Array<Upload>;
  onRowsScrollEnd: DataGridProProps["onRowsScrollEnd"];
  filter: IUploadsFilter;
  loading: boolean;
  onSortModelChange?: (model: GridSortModel) => void;
  excludeFields?: string[];
  handleRowClick?: GridEventListener<"rowClick"> | undefined;
  datagridPreferencesElement: DataGridPreferences;
  initialVisibilityModel: { [key in DataGridColumns]?: boolean };
  defaultText?: string;
  preferenceElementToUpdate: DataGrids;
};

const UploadsDataGrid = ({
  rows,
  onRowsScrollEnd,
  filter,
  loading,
  onSortModelChange,
  excludeFields,
  handleRowClick,
  datagridPreferencesElement,
  initialVisibilityModel,
  defaultText,
  preferenceElementToUpdate,
}: UploadsDatagridProps) => {
  const { user, updateDataGridPreferences } = useAuth();
  const columnsWidth = datagridPreferencesElement?.columnsWidth;
  const columnsAux: (GridColDef | undefined)[] = [
    excludeFields?.includes("report_datetime")
      ? undefined
      : {
          width: columnsWidth?.report_datetime || 150,
          field: "report_datetime",
          headerName: "Report Date",
          valueFormatter: (_, row: Upload) => {
            return getFormattedReportDate(row.report_datetime?.iso, true);
          },
          valueGetter: (_, row: Upload) => {
            return getFormattedReportDate(row.report_datetime?.iso);
          },
          filterable: false,
        },
    excludeFields?.includes("created")
      ? undefined
      : {
          width: columnsWidth?.created || 170,
          field: "created",
          headerName: "Created",
          valueFormatter: (_, row: Upload) => {
            if (!row.id) {
              return;
            }
            return toLocalAndUtcDates(row?.created).browser;
          },
          renderCell: (v) => {
            return (
              <span>
                <HumanDate date={v.row?.created} />
              </span>
            );
          },
          filterable: false,
        },
    excludeFields?.includes("started")
      ? undefined
      : {
          width: columnsWidth?.started || 170,
          field: "started",
          headerName: "Started",
          renderCell: (value: { row: Upload }) => (
            <span>
              <HumanDate date={value.row.started} />
            </span>
          ),
          valueFormatter: (_, row: Upload) => {
            if (!row.id || !row.started) {
              return;
            }
            return toLocalAndUtcDates(row.started).browser;
          },
          filterable: false,
        },
    excludeFields?.includes("finished")
      ? undefined
      : {
          width: columnsWidth?.finished || 170,
          field: "finished",
          headerName: "Finished",
          valueFormatter: (_, row: Upload) => {
            if (!row.id || !row.finished) {
              return;
            }
            return toLocalAndUtcDates(row.finished).browser;
          },
          renderCell: (v: { row: Upload }) => {
            return (
              <span>
                <HumanDate date={v.row?.finished} />
              </span>
            );
          },
          filterable: false,
        },
    excludeFields?.includes("upload_duration")
      ? undefined
      : {
          width: columnsWidth?.upload_duration || 140,
          field: "upload_duration",
          headerName: "Upload Duration",
          valueFormatter: (_, row: Upload) => {
            if (!row.id) {
              return;
            }
            const { started, finished } = row ?? {};
            const duration = calculateDateDuration(started, finished);
            return duration;
          },
          renderCell: (v: { row: Upload }) => {
            const { started, finished } = v.row ?? {};
            const duration = calculateDateDuration(started, finished);
            return <>{duration}</>;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("queued_duration")
      ? undefined
      : {
          width: columnsWidth?.queued_duration || 140,
          field: "queued_duration",
          headerName: "Queued Duration",
          valueFormatter: (_, row: Upload) => {
            if (!row.id) {
              return;
            }
            const { created, finished } = row ?? {};
            const duration = calculateDateDuration(created, finished);
            return duration;
          },
          renderCell: (v: { row: Upload }) => {
            const { created, finished } = v.row ?? {};
            const duration = calculateDateDuration(created, finished);
            return <>{duration}</>;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("project")
      ? undefined
      : {
          flex: columnsWidth?.project ? undefined : 1,
          width: columnsWidth?.project,
          minWidth: 100,
          field: "project",
          headerName: "Project name",
          valueGetter: (_, row: Upload) => {
            return row.project;
          },
          renderCell: (v: { row: Upload }) => {
            const row = v.row;
            return <>{row.project} </>;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("name")
      ? undefined
      : {
          flex: columnsWidth?.name ? undefined : 1,
          width: columnsWidth?.name,
          field: "name",
          headerName: "Upload",
          valueGetter: (_, row: Upload) => {
            return row.name;
          },
          renderCell: (v: { row: Upload }) => {
            const row = v.row;
            return <>{row.name} </>;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("destination")
      ? undefined
      : {
          flex: columnsWidth?.destination ? undefined : 1,
          width: columnsWidth?.destination,
          minWidth: 100,
          field: "destination",
          headerName: "Destination",
          renderCell: (v: { row: Upload }) => {
            const row = v.row;
            return <>{row?.handle?.destination} </>;
          },
          valueFormatter: (_, row: Upload) => {
            return row?.handle?.destination;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("purpose")
      ? undefined
      : {
          width: columnsWidth?.purpose || 100,
          field: "purpose",
          headerName: "Purpose",
          renderCell: (v: { row: Upload }) => (
            <CenteredDiv>
              <PurposeCell purpose={v.row?.purpose} />
            </CenteredDiv>
          ),
          valueFormatter: (_, row: Upload) => {
            return row?.purpose;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("status")
      ? undefined
      : {
          flex: columnsWidth?.status ? undefined : 1,
          width: columnsWidth?.status,
          minWidth: 100,
          field: "status",
          headerName: "Status",
          headerAlign: "right",
          align: "right",
          renderCell: (v: { row: Upload }) => {
            const upload = v.row;
            return (
              <CenteredDiv>
                <UploadStatusIcon status={upload.status} rowCount={upload.row_count} />
              </CenteredDiv>
            );
          },
          valueFormatter: (_, row: Upload) => {
            return row.status;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("action")
      ? undefined
      : {
          width: columnsWidth?.action || 100,
          field: "action",
          headerName: "Action",
          align: "right",
          headerAlign: "right",
          filterable: false,
          sortable: false,
          renderCell: ({ row }: { row: Upload }) => {
            return (
              <ActionsMenu>
                {({ closeModal }) => (
                  <>
                    <CopyToClipboardButton id={row.id} typeId="Upload ID" onClick={closeModal} />
                    <IfPermission permission={Permissions.ManagePipelines}>
                      {canUploadBeAbandoned(row.status) ? (
                        <UploadAbandonButton id={row.id} withText onClick={closeModal} />
                      ) : null}
                    </IfPermission>
                  </>
                )}
              </ActionsMenu>
            );
          },
        },
  ];

  const columns = columnsAux.filter((column) => column !== undefined);
  const columnsCopy = columns.slice();
  if (datagridPreferencesElement?.columnsOrder) {
    columnsCopy.sort((a, b) => {
      const aIndex = datagridPreferencesElement?.columnsOrder?.indexOf(a.field) ?? -1;
      const bIndex = datagridPreferencesElement?.columnsOrder?.indexOf(b.field) ?? -1;
      return aIndex - bIndex;
    });
  }

  const displayedColumns = hasPermission(Permissions.ManageAccountSettings, user)
    ? columnsCopy
    : columnsCopy.filter((column) => column.field !== "action");

  return (
    <DataGridPro
      disableColumnFilter
      loading={loading}
      rows={rows || []}
      columns={displayedColumns}
      disableColumnPinning
      disableDensitySelector
      onColumnWidthChange={(column) =>
        customColumnWidthChange(column, preferenceElementToUpdate, user, updateDataGridPreferences)
      }
      onColumnOrderChange={(column) => {
        customOnColumnOrderChange(column, preferenceElementToUpdate, user, updateDataGridPreferences, displayedColumns);
      }}
      columnVisibilityModel={datagridPreferencesElement?.columnVisibilityModel}
      onColumnVisibilityModelChange={(model) => {
        updateDataGridPreferences(preferenceElementToUpdate, {
          columnsOrder: datagridPreferencesElement?.columnsOrder,
          columnsWidth: datagridPreferencesElement?.columnsWidth,
          columnVisibilityModel: model,
        });
      }}
      getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
      onRowClick={handleRowClick}
      slots={{
        columnsPanel: CustomColumnsPanel,
        noRowsOverlay: EmptyRows,
        noResultsOverlay: EmptyRows,
      }}
      slotProps={{
        root: { "data-testid": "uploads-table" },
        loadingOverlay: {
          variant: "linear-progress",
          noRowsVariant: "linear-progress",
        },
        noRowsOverlay: {
          isFiltering: isFilteringUploads(filter),
          defaultText: defaultText,
        },
        row: {
          "data-testid": "upload-row",
          style: {
            cursor: "pointer",
          },
        },
        panel: {
          //@ts-ignore
          sx: {
            "& .MuiDataGrid-filterFormValueInput": {
              width: "350px",
              marginLeft: "8px",
              justifyContent: "center",
            },
          },
        },
      }}
      filterMode="server"
      onRowsScrollEnd={onRowsScrollEnd}
      onSortModelChange={onSortModelChange}
      hideFooter
      initialState={{
        columns: {
          columnVisibilityModel: datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel,
        },
        sorting: {
          sortModel: getInitialSortModel(filter),
        },
      }}
      sortingMode="server"
    />
  );
};

export { UploadsDataGrid };
